import React, { useEffect, useState, useMemo, useCallback } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { apiService } from '../services/apiService';
import { useToast } from '../components/Toast';
import { useAuth } from '../services/authContext';

const DURATIONS   = ['6 Months', '1 Year', '2 Years', '3 Years'];
const SEMESTERS   = ['Semester 1', 'Semester 2'];
const BATCHES     = ['A', 'B', 'C', 'D', 'E'];
const PROG_COLORS = { 'Film Studies': '#D32F2F', 'Information Technology': '#1976D2', 'Graphic Design': '#388E3C' };

const emptyProg  = { name: '', code: '', description: '', durationOptions: ['6 Months','1 Year','2 Years','3 Years'], active: true };
const emptyClass = { programmeId: '', programme: '', yearLevel: 1, semester: 'Semester 1', batchLabel: 'A', duration: '1 Year', academicYear: new Date().getFullYear(), maxStudents: 40, teacherId: '', description: '', status: 'active' };

const Programmes = () => {
  const { user }                          = useAuth();
  const { toast }                         = useToast();
  const isAdmin = user?.role === 'admin';
  const canManageBatches = user?.role === 'admin' || user?.role === 'teacher';
  const [programmes, setProgrammes]       = useState([]);
  const [classes, setClasses]             = useState([]);
  const [teachers, setTeachers]           = useState([]);
  const [modules, setModules]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [search, setSearch]               = useState('');
  const [expandedProg, setExpandedProg]   = useState(null);
  // Programme modal
  const [progModal, setProgModal]         = useState(false);
  const [editProg, setEditProg]           = useState(null);
  const [progForm, setProgForm]           = useState(emptyProg);
  // Batch/class modal
  const [classModal, setClassModal]       = useState(false);
  const [editClass, setEditClass]         = useState(null);
  const [classForm, setClassForm]         = useState(emptyClass);
  // Modules panel
  const [modulesPanel, setModulesPanel]   = useState(null);
  const [addModuleId, setAddModuleId]   = useState('');
  const [addTeacherId, setAddTeacherId]   = useState('');
  const [modSaving, setModSaving]         = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [progs, cls, tchs, mods] = await Promise.all([
        apiService.getAll('programmes-list'),
        apiService.getAll('classes'),
        apiService.getAll('teachers'),
        apiService.getAll('modules'),
      ]);
      setProgrammes(progs || []);
      setClasses(cls   || []);
      setTeachers(tchs || []);
      setModules(mods || []);
    } catch (err) { toast(err.response?.data?.message || 'Failed to load.', 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Group classes by programme
  const classesByProg = useMemo(() =>
    classes.reduce((acc, c) => {
      const pid = c.programmeId || 'unassigned';
      if (!acc[pid]) acc[pid] = [];
      acc[pid].push(c);
      return acc;
    }, {}), [classes]);

  const filtered = useMemo(() =>
    programmes.filter(p => p.name.toLowerCase().includes(search.toLowerCase())),
    [programmes, search]);

  // Programme CRUD
  const handleProgSubmit = async e => {
    e.preventDefault();
    try {
      setSaving(true);
      if (editProg) {
        await apiService.update('programmes-list', editProg.id, progForm);
        toast('Programme updated.', 'success');
      } else {
        await apiService.create('programmes-list', progForm);
        toast('Programme created.', 'success');
      }
      setProgModal(false); setEditProg(null); setProgForm(emptyProg);
      await load();
    } catch (err) { toast(err.response?.data?.message || 'Failed to save.', 'error'); }
    finally { setSaving(false); }
  };

  const handleDeleteProg = async id => {
    if (!window.confirm('Delete this programme? All linked batches will be removed.')) return;
    try {
      await apiService.delete('programmes-list', id);
      toast('Programme deleted.', 'success');
      await load();
    } catch (err) { toast(err.response?.data?.message || 'Failed to delete.', 'error'); }
  };

  // Batch/class CRUD
  const openAddClass = prog => {
    setEditClass(null);
    setClassForm({ ...emptyClass, programmeId: prog.id, programme: prog.name, academicYear: new Date().getFullYear() });
    setClassModal(true);
  };

  const openEditClass = cls => {
    setEditClass(cls);
    setClassForm({
      programmeId: cls.programmeId || '', programme: cls.programme || '',
      yearLevel: cls.yearLevel || 1, semester: cls.semester || 'Semester 1',
      batchLabel: cls.batchLabel || 'A', duration: cls.duration || '1 Year',
      academicYear: cls.academicYear || new Date().getFullYear(),
      maxStudents: cls.maxStudents || 40, teacherId: cls.teacher?._id || '',
      description: cls.description || '', status: cls.status || 'active',
    });
    setClassModal(true);
  };

  const handleClassSubmit = async e => {
    e.preventDefault();
    try {
      setSaving(true);
      // Auto-generate name
      const prog = programmes.find(p => p.id === Number(classForm.programmeId));
      const name = `${prog?.name || classForm.programme} Y${classForm.yearLevel} ${classForm.semester} Batch ${classForm.batchLabel}`;
      const payload = { ...classForm, name, teacherId: classForm.teacherId || null };
      if (editClass) {
        await apiService.update('classes', editClass._id, payload);
        toast('Batch updated.', 'success');
      } else {
        await apiService.create('classes', payload);
        toast('Batch created.', 'success');
      }
      setClassModal(false); setEditClass(null); setClassForm(emptyClass);
      await load();
    } catch (err) { toast(err.response?.data?.message || 'Failed to save batch.', 'error'); }
    finally { setSaving(false); }
  };

  const handleDeleteClass = async id => {
    if (!window.confirm('Delete this batch? Students and modules will be unlinked.')) return;
    try {
      await apiService.delete('classes', id);
      toast('Batch deleted.', 'success');
      await load();
    } catch (err) { toast(err.response?.data?.message || 'Failed to delete.', 'error'); }
  };

  // Modules panel
  const openModules = async cls => {
    try {
      const subs = await apiService.get(`modules/class/${cls._id}`);
      setModulesPanel({ cls, modules: Array.isArray(subs) ? subs : [] });
      setAddModuleId(''); setAddTeacherId('');
    } catch { setModulesPanel({ cls, modules: [] }); }
  };

  const refreshModules = async () => {
    if (!modulesPanel) return;
    const mods = await apiService.get(`modules/class/${modulesPanel.cls._id}`);
    setModulesPanel(p => ({ ...p, modules: Array.isArray(mods) ? mods : [] }));
  };

  const handleAddModule = async () => {
    if (!addModuleId) { toast('Select a module.', 'warning'); return; }
    try {
      setModSaving(true);
      await apiService.post('modules/class', { classId: modulesPanel.cls._id, moduleId: addModuleId, teacherId: addTeacherId || null });
      toast('Module added.', 'success');
      setAddModuleId(''); setAddTeacherId('');
      await refreshModules();
    } catch (err) { toast(err.response?.data?.message || 'Failed to add module.', 'error'); }
    finally { setModSaving(false); }
  };

  const handleRemoveModule = async moduleId => {
    if (!window.confirm('Remove this module from the batch?')) return;
    try {
      await apiService.raw.delete(`modules/class/${modulesPanel.cls._id}/${moduleId}`);
      toast('Module removed.', 'success');
      await refreshModules();
    } catch (err) { toast(err.response?.data?.message || 'Failed to remove.', 'error'); }
  };

  const unassigned = useMemo(() => {
    if (!modulesPanel) return [];
    const assigned = new Set(modulesPanel.modules.map(s => s._id));
    return modules.filter(s => !assigned.has(s._id));
  }, [modulesPanel, modules]);

  // Group batches by year then semester
  const groupBatches = batches => {
    const byYear = {};
    batches.forEach(b => {
      const y = b.yearLevel || 1;
      const s = b.semester || 'No Semester';
      if (!byYear[y]) byYear[y] = {};
      if (!byYear[y][s]) byYear[y][s] = [];
      byYear[y][s].push(b);
    });
    return byYear;
  };

  const statusColor = s => ({ active: '#388E3C', completed: '#1976D2', cancelled: '#c62828' }[s] || '#666');

  if (loading) return <LoadingSpinner message="Loading programmes..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Academic Programmes</h1>
        {isAdmin && <button className="btn btn-primary" onClick={() => { setEditProg(null); setProgForm(emptyProg); setProgModal(true); }}>+ Add Programme</button>}
      </div>

      <div className="search-container">
        <input type="text" placeholder="Search programmes..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {filtered.length === 0 && <div className="no-data-message">No programmes found.</div>}

      {filtered.map(prog => {
        const progColor   = PROG_COLORS[prog.name] || '#546E7A';
        const progClasses = classesByProg[prog.id] || [];
        const isExpanded  = expandedProg === prog.id;
        const grouped     = groupBatches(progClasses);

        return (
          <div key={prog.id} style={{ marginBottom: '1.5rem', border: `1px solid ${progColor}44`, borderRadius: 10, overflow: 'hidden' }}>
            {/* Programme header */}
            <div style={{ background: `${progColor}11`, borderBottom: `2px solid ${progColor}`, padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', cursor: 'pointer' }}
              onClick={() => setExpandedProg(isExpanded ? null : prog.id)}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ background: progColor, color: '#fff', padding: '0.2rem 0.6rem', borderRadius: 6, fontSize: '0.8rem', fontWeight: 700 }}>{prog.code}</span>
                  <h2 style={{ margin: 0, color: progColor }}>{prog.name}</h2>
                  {!prog.active && <span style={{ background: '#f8d7da', color: '#721c24', padding: '0.1rem 0.4rem', borderRadius: 4, fontSize: '0.75rem' }}>Inactive</span>}
                </div>
                {prog.description && <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: 'var(--text-light)' }}>{prog.description}</p>}
                <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '0.3rem' }}>
                  Durations: {prog.durationOptions?.join(' · ')} &nbsp;|&nbsp;
                  {progClasses.length} batch{progClasses.length !== 1 ? 'es' : ''} &nbsp;|&nbsp;
                  {prog.studentCount} student{prog.studentCount !== 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {canManageBatches && (
                  <button className="btn btn-secondary" style={{ fontSize: '0.82rem' }} onClick={e => { e.stopPropagation(); openAddClass(prog); }}>+ Add Batch</button>
                )}
                {isAdmin && <button className="btn-icon" onClick={e => { e.stopPropagation(); setEditProg(prog); setProgForm({ name: prog.name, code: prog.code, description: prog.description || '', durationOptions: prog.durationOptions, active: prog.active }); setProgModal(true); }}>Edit</button>}
                {isAdmin && <button className="btn-icon" style={{ color: '#c62828' }} onClick={e => { e.stopPropagation(); handleDeleteProg(prog.id); }}>Delete</button>}
                <span style={{ fontSize: '1.2rem', color: progColor }}>{isExpanded ? '▲' : '▼'}</span>
              </div>
            </div>

            {/* Expanded batches */}
            {isExpanded && (
              <div style={{ padding: '1rem 1.25rem' }}>
                {progClasses.length === 0 ? (
                  <div className="no-data-message">No batches yet. {canManageBatches && 'Click "+ Add Batch" to create one.'}</div>
                ) : (
                  Object.entries(grouped).sort(([a], [b]) => a - b).map(([year, semesters]) => (
                    <div key={year} style={{ marginBottom: '1.25rem' }}>
                      <h3 style={{ color: progColor, margin: '0 0 0.75rem', fontSize: '1rem' }}>Year {year}</h3>
                      {Object.entries(semesters).map(([sem, batches]) => (
                        <div key={sem} style={{ marginBottom: '1rem' }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-light)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{sem}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem' }}>
                            {batches.map(cls => (
                              <div key={cls._id} style={{ background: 'var(--card-bg)', border: `1px solid ${progColor}33`, borderLeft: `4px solid ${progColor}`, borderRadius: 8, padding: '0.9rem 1rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem' }}>
                                      <strong>Batch {cls.batchLabel}</strong>
                                      <span style={{ background: `${statusColor(cls.status)}22`, color: statusColor(cls.status), padding: '0.1rem 0.35rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 }}>{cls.status}</span>
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>
                                      {cls.duration} · {cls.academicYear}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>
                                      👨🏫 {cls.teacher?.name || 'No lecturer'}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>
                                      👥 {cls.studentCount}/{cls.maxStudents} students
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                    <button className="btn-icon" style={{ fontSize: '0.75rem', color: '#1976D2' }} onClick={() => openModules(cls)}>Modules</button>
                                    {canManageBatches && <button className="btn-icon" style={{ fontSize: '0.75rem' }} onClick={() => openEditClass(cls)}>Edit</button>}
                                    {isAdmin && <button className="btn-icon" style={{ fontSize: '0.75rem', color: '#c62828' }} onClick={() => handleDeleteClass(cls._id)}>Delete</button>}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Programme Modal */}
      {isAdmin && (
        <Modal isOpen={progModal} onClose={() => setProgModal(false)} title={editProg ? 'Edit Programme' : 'Add Programme'}>
          <form onSubmit={handleProgSubmit} className="form">
            <div className="form-row">
              <div className="form-group">
                <label>Programme Name *</label>
                <input value={progForm.name} onChange={e => setProgForm(p => ({ ...p, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>Code * (e.g. FS, IT, GD)</label>
                <input value={progForm.code} onChange={e => setProgForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} required maxLength={10} />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea rows={3} value={progForm.description} onChange={e => setProgForm(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Available Durations</label>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {['6 Months','1 Year','2 Years','3 Years'].map(d => (
                  <label key={d} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={progForm.durationOptions?.includes(d)}
                      onChange={e => setProgForm(p => ({ ...p, durationOptions: e.target.checked ? [...(p.durationOptions || []), d] : (p.durationOptions || []).filter(x => x !== d) }))} />
                    {d}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="prog-active" checked={progForm.active} onChange={e => setProgForm(p => ({ ...p, active: e.target.checked }))} />
              <label htmlFor="prog-active" style={{ marginBottom: 0 }}>Active</label>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setProgModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : editProg ? 'Update' : 'Create Programme'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Batch/Class Modal */}
      {canManageBatches && (
        <Modal isOpen={classModal} onClose={() => setClassModal(false)} title={editClass ? 'Edit Batch' : 'Add Batch'}>
          <form onSubmit={handleClassSubmit} className="form">
            <div className="form-row">
              <div className="form-group">
                <label>Programme *</label>
                <select value={classForm.programmeId} onChange={e => { const p = programmes.find(x => x.id === Number(e.target.value)); setClassForm(f => ({ ...f, programmeId: e.target.value, programme: p?.name || '' })); }} required>
                  <option value="">Select Programme</option>
                  {programmes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Duration</label>
                <select value={classForm.duration} onChange={e => setClassForm(f => ({ ...f, duration: e.target.value }))}>
                  {DURATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Year Level</label>
                <select value={classForm.yearLevel} onChange={e => setClassForm(f => ({ ...f, yearLevel: Number(e.target.value) }))}>
                  {[1,2,3].map(y => <option key={y} value={y}>Year {y}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Semester</label>
                <select value={classForm.semester} onChange={e => setClassForm(f => ({ ...f, semester: e.target.value }))}>
                  {SEMESTERS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Batch Label</label>
                <select value={classForm.batchLabel} onChange={e => setClassForm(f => ({ ...f, batchLabel: e.target.value }))}>
                  {BATCHES.map(b => <option key={b} value={b}>Batch {b}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Academic Year</label>
                <input type="number" value={classForm.academicYear} onChange={e => setClassForm(f => ({ ...f, academicYear: e.target.value }))} min={2020} max={2040} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Max Students</label>
                <input type="number" value={classForm.maxStudents} onChange={e => setClassForm(f => ({ ...f, maxStudents: e.target.value }))} min={1} />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={classForm.status} onChange={e => setClassForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Assign Lecturer</label>
                <select value={classForm.teacherId} onChange={e => setClassForm(f => ({ ...f, teacherId: e.target.value }))}>
                  <option value="">Select Lecturer</option>
                  {teachers.map(t => <option key={t._id} value={t._id}>{t.firstName} {t.lastName}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea rows={2} value={classForm.description} onChange={e => setClassForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setClassModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : editClass ? 'Update Batch' : 'Create Batch'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modules Panel */}
      <Modal isOpen={!!modulesPanel} onClose={() => setModulesPanel(null)} title={`Modules — ${modulesPanel?.cls?.name}`}>
        {modulesPanel && (
          <div>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Assigned Modules ({modulesPanel.modules.length})</h3>
            {modulesPanel.modules.length === 0 ? (
              <div className="no-data-message" style={{ marginBottom: '1.5rem' }}>No modules assigned yet.</div>
            ) : (
              <div className="table-responsive" style={{ marginBottom: '1.5rem' }}>
                <table className="data-table">
                  <thead><tr><th>Code</th><th>Module</th><th>Lecturer</th><th>Action</th></tr></thead>
                  <tbody>
                    {modulesPanel.modules.map(s => (
                      <tr key={s._id}>
                        <td><strong>{s.code}</strong></td>
                        <td>{s.name}</td>
                        <td>{s.teacherName || 'Not assigned'}</td>
                        <td><button className="btn-icon" style={{ color: '#c62828' }} onClick={() => handleRemoveModule(s._id)}>Remove</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Add Module</h3>
            {unassigned.length === 0 ? (
              <div style={{ color: 'var(--text-light)', fontSize: '0.85rem' }}>All modules are already assigned to this batch.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Module</label>
                    <select value={addModuleId} onChange={e => setAddModuleId(e.target.value)}>
                      <option value="">Select Module</option>
                      {unassigned.map(s => <option key={s._id} value={s._id}>{s.code} — {s.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Assign Lecturer</label>
                    <select value={addTeacherId} onChange={e => setAddTeacherId(e.target.value)}>
                      <option value="">Select Lecturer</option>
                      {teachers.map(t => <option key={t._id} value={t._id}>{t.firstName} {t.lastName}</option>)}
                    </select>
                  </div>
                </div>
                <button className="btn btn-primary" onClick={handleAddModule} disabled={modSaving} style={{ alignSelf: 'flex-start' }}>
                  {modSaving ? 'Adding...' : '+ Add Module to Batch'}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Programmes;
